import React from 'react';

import { useSelector, useStore } from 'react-redux';

import { isEqual } from 'lodash-es';
import PropTypes from 'prop-types';

import Splash from '@asteria/component-splash';

import * as IntegrationStore from '@asteria/datalayer/stores/integrations';

import { cn } from '@asteria/utils-funcs/classes';

import './styles.scss';

/**
 * @template T
 * @param { T[] } integrations
 * @returns { T | undefined }
 */
function findBrokenIntegration(integrations) {
	return (integrations ?? [])
		.filter((object) => object?.actions?.length)
		.find((object) => {
			const actions = object?.actions ?? [];

			return actions
				.filter(
					({ action: event, status = 'PENDING' }) =>
						['scope.missing', 'scope.update'].includes(event) &&
						(!status || status === 'PENDING'),
				)
				.some((action) => {
					const data = action?.data;

					return ['customers', 'suppliers', 'clients'].includes(
						data?.type,
					);
				});
		});
}

function useVariant() {
	return useSelector((store) => {
		const integrations = IntegrationStore.selectors.integrations(store, {
			type: 'erp',
		});

		if (!integrations.length) {
			return 'erp';
		}

		const brokenIntegration = findBrokenIntegration(integrations);

		if (brokenIntegration) {
			return 'scope';
		}

		return 'extra';
	}, isEqual);
}

const ClientSplash = (props) => {
	const { className, onAction } = props;

	const store = useStore();
	const variant = useVariant();

	const handleAction = React.useCallback(
		(action, data) => {
			if (action === 'erp:create') {
				return onAction?.('go', {
					path: '/onboarding/connect',
					state: { type: 'erp' },
				});
			}

			if (action === 'erp:update') {
				const integration = findBrokenIntegration(
					IntegrationStore.selectors.integrations(store.getState(), {
						type: 'erp',
					}),
				);

				const id = integration?._id ?? integration?.id;

				return onAction?.('go', { path: `/onboarding/${id}/status` });
			}

			onAction(action, data);
		},
		[onAction, store],
	);

	const actions = React.useMemo(
		() => ({ onAction: handleAction }),
		[handleAction],
	);

	let path = 'pages.client.splash';

	if (variant === 'scope') {
		path = 'pages.client.splash.scope';
	} else if (variant === 'extra') {
		path = 'pages.client.splash.extra';
	}

	return (
		<Splash
			className={cn(
				'asteria-component__client-splash',
				'asteria--variant-client',
				className,
			)}
			path={path}
			actions={actions}
		/>
	);
};

ClientSplash.propTypes = {
	className: PropTypes.string,
	onAction: PropTypes.func,
};

ClientSplash.defaultProps = {};

export default ClientSplash;
